import {
  createFlagEvaluationTracker,
  initAnalyticsClient,
  MIXPANEL_PLUGIN,
  TRACK_DATA_ATTRIBUTES,
} from "@analytics/client";

const isMixpanelEnabled = Boolean(
  import.meta.env.VITE_MIXPANEL_TOKEN && !import.meta.env.VITE_LOCAL
);

const analyticsClient = initAnalyticsClient({
  app: "spirii-ui",
  debug: import.meta.env.DEV,
  plugins: isMixpanelEnabled
    ? [
        {
          type: MIXPANEL_PLUGIN,
          token: import.meta.env.VITE_MIXPANEL_TOKEN as string,
          instanceName: "spirii-ui",
          options: {
            // Tightened autocapture. IMPORTANT: mixpanel merges this object over
            // its defaults (all `true`), so unwanted captures must be set to
            // `false` explicitly, not omitted.
            // On: pageview (the page-view source across all microfrontends),
            //   click, submit, rage_click (frustration signal → Session Replay).
            // Off: input + scroll (noise / PII), dead_click (noise).
            autocapture: {
              pageview: "url-with-path-and-query-string",
              click: true,
              dead_click: true,
              rage_click: true,
              input: false,
              scroll: false,
              submit: true,
              // Record declarative `data-track-*` attributes as
              // `$el_attr__data-track-*` properties on captured clicks (we don't
              // emit custom events). Autocapture can't read arbitrary `data-*`,
              // so every attribute name must be listed here.
              capture_extra_attrs: [...TRACK_DATA_ATTRIBUTES],
            },
            // Noisy/heavy autocapture DOM props (coordinates, $elements/$target,
            // $el_*, layout dims) are decluttered via Lexicon "Hide Property"
            // (Data Governance), not dropped at the SDK — so Mixpanel's autocapture
            // features (heatmaps, Session Replay element context) keep working.
            api_host: "https://api-eu.mixpanel.com",
            persistence: "localStorage",
          },
        },
      ]
    : [],
});

// Sink for OpenFeature flag evaluations: registers each flag's variant as a
// super property and fires `$experiment_started` (deduped). Wired into
// initializeFeatureFlags' `onEvaluation`; covers connect via the shared SDK.
const trackFlagEvaluation = createFlagEvaluationTracker(analyticsClient, {
  enabled: isMixpanelEnabled,
});

export { analyticsClient, isMixpanelEnabled, trackFlagEvaluation };
